import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { GetApp as GetAppIcon } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid';

import { AppDataGridColumnSettingsButton, JobIdAutocomplete, JobNameAutocomplete, JobNameWithScheduleLabel, OrderIdAutocomplete, Page, ReportTable } from 'components';
import { useSession } from 'store';
import { t } from 'i18n';
import { callApi, download, formatFieldName } from 'utils';
import { allTrackers } from 'consts';

const defaultFilter = {idtype: 'parent', id: '', orderId: '', jobId: '', q: '', start_date: null, end_date: null};
const defaultLimit = 100;
const idColumn = { minWidth: 64, flex: 0.45 };
const metricColumn = { minWidth: 76, flex: 0.55 };
const rateColumn = { minWidth: 84, flex: 0.6 };
const completionRateColumn = { ...rateColumn, numeric: true };
const trackerColumnNames = allTrackers.map((_, index) => `tracker_${index}`);
const trackerColumns = Object.fromEntries(allTrackers.map((label, trackerIndex) => [
  trackerColumnNames[trackerIndex],
  { label, trackerIndex, minWidth: 148, flex: 1.15 },
]));
const columns = {
  date: { minWidth: 104, flex: 0.75 },
  parent: idColumn,
  job: idColumn,
  name: { minWidth: 220, flex: 3, truncate: true },
  ad_type: { minWidth: 76, flex: 0.55 },
  daypart: { minWidth: 108, flex: 0.85 },
  scheduled_imp: { ...metricColumn, numeric: true },
  scheduled_clk: { ...metricColumn, numeric: true },
  imps: metricColumn,
  imp_completion_rate: completionRateColumn,
  clicks: metricColumn,
  clk_completion_rate: completionRateColumn,
  ctr: metricColumn,
  stay: metricColumn,
  landing_rate: rateColumn,
  jump2: rateColumn,
  ...trackerColumns,
};
const getDefaultDateRange = () => {
  const endDate = dayjs();
  return {
    start_date: endDate.subtract(6, 'day'),
    end_date: endDate,
  };
};
const getDefaultFilter = () => ({
  ...defaultFilter,
  ...getDefaultDateRange(),
});
const buildDailyReportParams = (searchParams, {pagination=true} = {}) => {
  const params = new URLSearchParams(searchParams);
  const defaultDateRange = getDefaultDateRange();

  params.get('start') || params.set('start', defaultDateRange.start_date.format('YYYY-MM-DD'));
  params.get('end') || params.set('end', defaultDateRange.end_date.format('YYYY-MM-DD'));

  if (pagination) {
    params.set('limit', defaultLimit);
  } else {
    params.delete('page');
    params.delete('limit');
  }

  return params;
};
const sanitizeDownloadName = (name) => String(name || '')
  .trim()
  .replace(/[\\/:*?"<>|]+/g, '-')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .slice(0, 80);
const getDailyReportDownloadBaseName = async (params) => {
  if (params.get('idtype') !== 'parent') return '日报表';

  const id = Number(params.get('id'));
  if (id <= 0) return '日报表';

  try {
    const lookupParams = new URLSearchParams({
      gid: String(id),
      q: `id:${id}`,
      limit: '1',
    });
    const token = localStorage.getItem('token');
    const resp = await fetch(`/api/jobs?${lookupParams.toString()}`, {
      headers: {
        Accept: 'application/json',
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
      },
    });
    if (!resp.ok) throw new Error(`failed to load order ${id}`);

    const res = await resp.json();
    const name = res.rows?.[0]?.name?.replace(/ 1$/, '');
    const suffix = sanitizeDownloadName([name, id].filter(Boolean).join('-'));
    return suffix ? `日报表-${suffix}` : `日报表-${id}`;
  } catch (ex) {
    console.error(ex);
    return `日报表-${id}`;
  }
};
const filterItemSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
};
const filterToolbarSx = {
  width: '100%',
  flexWrap: 'wrap',
};
const textFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1 1 210px', md: '1 1 170px'},
  minWidth: {xs: '100%', sm: 160},
};
const dateFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1.2 1 300px'},
  minWidth: {xs: '100%', sm: 300},
};
const filterActionsSx = {
  display: 'flex',
  flex: {xs: '1 1 100%', sm: '0 0 auto'},
  gap: 1,
  whiteSpace: 'nowrap',
};
const filterControlSx = {
  p: 0,
  flex: '1 1 130px',
  minWidth: 0,
};
const datePickerSx = {
  flex: '1 1 120px',
  minWidth: 0,
  '& .MuiInputBase-input': {p: '6px'},
};
const filterButtonSx = {
  minWidth: 88,
  flexShrink: 0,
};

function formatTrackerCell(row, trackerIndex) {
  const count = Number(row.tracker_cnt?.[trackerIndex]) || 0;
  if (!count) return '--';

  const landing = Number(row.landing) || 0;
  const stay = Number(row.tracker_stay?.[trackerIndex]);
  const bounce = Number(row.tracker_bounce?.[trackerIndex]) || 0;
  return [
    landing ? `${(100 * count / landing).toFixed(1)}%` : '--',
    Number.isFinite(stay) ? `${Math.ceil(stay)}s` : '--',
    bounce ? `${(100 * bounce / count).toFixed(1)}%` : '--',
  ].join(' / ');
}

function renderCellValue(col, row) {
  const trackerIndex = columns[col]?.trackerIndex;
  if (Number.isInteger(trackerIndex)) return formatTrackerCell(row, trackerIndex);
  return col === 'name' ? <JobNameWithScheduleLabel job={row} name={row[col]} compact /> : row[col];
}

const DailyReports = ({embedded, initialSearchParams, showToolbar, showScopeFilters, showDownload, showTrackerColumns, columnSettingsTableId}) => {
  const location = useLocation();
  const [routeSearchParams, setRouteSearchParams] = useSearchParams();
  const [embeddedSearchParams, setEmbeddedSearchParamsState] = useState(
    () => new URLSearchParams(initialSearchParams)
  );
  const setEmbeddedSearchParams = useCallback((next) => {
    setEmbeddedSearchParamsState((prev) => {
      const mutablePrev = new URLSearchParams(prev);
      const value = typeof next === 'function' ? next(mutablePrev) : next;
      return new URLSearchParams(value);
    });
  }, []);
  const searchParams = embedded ? embeddedSearchParams : routeSearchParams;
  const setSearchParams = embedded ? setEmbeddedSearchParams : setRouteSearchParams;
  const reportGridApiRef = useGridApiRef();
  const session = useSession((state) => state.session);
  const [data, setData] = useState(null);
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);
  const activeRequestRef = useRef('');
  const inFlightRequestsRef = useRef(new Set());
  const [filter, setFilter] = useState(() => {
    const defaultDateRange = getDefaultDateRange();
    const nextFilter = Object.fromEntries(Object.keys(defaultFilter).map(k => {
      let v;
      if (k.endsWith('_date')) {
        v = searchParams.get(k.slice(0, -5));
        v = v ? dayjs(v) : defaultDateRange[k];
      } else {
        v = searchParams.get(k) || defaultFilter[k];
      }
      return [k, v];
    }));
    if (nextFilter.id) {
      if (nextFilter.idtype === 'id') {
        nextFilter.jobId = nextFilter.id;
      } else {
        nextFilter.orderId = nextFilter.id;
      }
    }
    return nextFilter;
  });

  const dim = embedded ? 'jobdaily' : (location.pathname.slice(9) || 'jobdaily'); // /reports/abc...
  const page = searchParams.get('page')|0;
  const limit = defaultLimit;

  useEffect(() => {
    if (!searchParams.has('limit')) return;
    setSearchParams((prev) => {
      prev.delete('limit');
      return prev;
    });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const params = buildDailyReportParams(searchParams);
    const requestKey = `${dim}?${params.toString()}`;
    activeRequestRef.current = requestKey;
    if (inFlightRequestsRef.current.has(requestKey)) return;

    inFlightRequestsRef.current.add(requestKey);
    callApi(`/reports/${requestKey}`)
      .then((res) => {
        if (activeRequestRef.current === requestKey) {
          setData(res);
        }
      })
      .catch(console.error)
      .finally(() => {
        inFlightRequestsRef.current.delete(requestKey);
      });
  }, [dim, searchParams]);

  const handleFilter = useCallback((ev) => setFilter(prev => ({...prev, [ev.target.name]: ev.target.value})), []);
  const handleOrderIdFilter = useCallback((ev) => {
    const orderId = ev.target.value;
    setFilter(prev => ({
      ...prev,
      idtype: orderId ? 'parent' : (prev.jobId ? 'id' : 'parent'),
      id: orderId || prev.jobId || '',
      orderId,
      jobId: orderId ? '' : prev.jobId,
    }));
  }, []);
  const handleJobIdFilter = useCallback((ev) => {
    const jobId = ev.target.value;
    setFilter(prev => ({
      ...prev,
      idtype: jobId ? 'id' : 'parent',
      id: jobId || prev.orderId || '',
      jobId,
    }));
  }, []);
  const changeDate = (name, val) => setFilter(prev => ({...prev, [name]: val}));
  const submitFilter = (ev) => {
    ev?.preventDefault();
    const prev = new URLSearchParams(searchParams);
    prev.delete('page');
    prev.delete('limit');

    const sp = new URLSearchParams();
    const id = filter.jobId || filter.orderId || filter.id;
    if (id) {
      sp.set('idtype', filter.jobId ? 'id' : (filter.orderId ? 'parent' : filter.idtype));
      sp.set('id', id.trim());
    }
    filter.q.trim() && sp.set('q', filter.q.trim());
    filter.start_date && sp.set('start', filter.start_date.format('YYYY-MM-DD'));
    filter.end_date && sp.set('end', filter.end_date.format('YYYY-MM-DD'));

    if (prev.toString() !== sp.toString()) {
      setSearchParams(sp);
    }
  };
  const resetFilter = () => setFilter((prev) => {
    const nextFilter = getDefaultFilter();
    if (showScopeFilters) return nextFilter;

    return {
      ...nextFilter,
      idtype: prev.idtype,
      id: prev.id,
      orderId: prev.orderId,
      jobId: prev.jobId,
    };
  });

  const handlePagination = (k, v) => {
    setSearchParams((prev) => {
      if (k === 'page') {
        v > 0 ? prev.set(k, v) : prev.delete(k);
      } else {
        v !== defaultLimit ? prev.set(k, v) : prev.delete(k);
        prev.delete('page'); // return from first page if change limit
      }
      return prev;
    });
  };
  const exportDailyReport = async () => {
    const params = buildDailyReportParams(searchParams, {pagination: false});
    const basename = await getDailyReportDownloadBaseName(params);
    download(`/reports/export?type=jobdaily&${params.toString()}`, basename);
  };
  const columnSettingsButtonId = `daily-report-${dim}-column-settings-button`;
  const columnSettingsPanelId = `daily-report-${dim}-column-settings-panel`;

  const title = data?.title || 'Report';
  const displayedData = useMemo(() => {
    if (!data || !showTrackerColumns) return data;
    const dataColumns = data.columns || [];
    const existingColumns = new Set(dataColumns);
    return {
      ...data,
      columns: [...dataColumns, ...trackerColumnNames.filter(name => !existingColumns.has(name))],
    };
  }, [data, showTrackerColumns]);
  const reportContent = (
      <Stack spacing={1} sx={{height: '100%', width: '100%', minHeight: 0, p: embedded ? 0 : 2, pt: embedded ? 0 : 1}}>
        {showToolbar && (
        <Box component="form" onSubmit={submitFilter} sx={{flexShrink: 0}}>
          <Grid container spacing={1} alignItems="center" sx={filterToolbarSx}>
            {showScopeFilters && (
              <>
                <Grid item sx={textFilterItemSx}>
                  <Typography sx={{mr: 0.5, flexShrink: 0}}>订单:</Typography>
                  <OrderIdAutocomplete
                    sx={filterControlSx}
                    name="id"
                    value={filter.orderId}
                    onChange={handleOrderIdFilter}
                  />
                </Grid>
                <Grid item sx={textFilterItemSx}>
                  <Typography sx={{mr: 0.5, flexShrink: 0}}>任务:</Typography>
                  <JobIdAutocomplete
                    groupId={filter.orderId}
                    sx={filterControlSx}
                    name="id"
                    value={filter.jobId}
                    onChange={handleJobIdFilter}
                  />
                </Grid>
                <Grid item sx={textFilterItemSx}>
                  <Typography sx={{mr: 0.5, flexShrink: 0}}>名称:</Typography>
                  <JobNameAutocomplete
                    sx={filterControlSx}
                    name="q"
                    value={filter.q}
                    onChange={handleFilter}
                  />
                </Grid>
              </>
            )}
            <Grid item sx={dateFilterItemSx}>
              <Typography sx={{mr: 0.5, flexShrink: 0}}>日期:</Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0}}>
                <DatePicker
                  sx={datePickerSx}
                  value={filter.start_date}
                  views={['year', 'month', 'day']}
                  onChange={val => changeDate('start_date', val)}
                  dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                />
                <Typography component="span" sx={{flexShrink: 0}}>~</Typography>
                <DatePicker
                  sx={datePickerSx}
                  value={filter.end_date}
                  views={['year', 'month', 'day']}
                  minDate={filter.start_date}
                  onChange={val => changeDate('end_date', val)}
                  dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                />
              </Box>
            </Grid>
            <Grid item sx={filterActionsSx}>
              <Button
                type="submit"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
              >搜索</Button>
              <Button
                type="button"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
                onClick={resetFilter}
              >重置</Button>
              {showDownload && (
                <Button
                  type="button"
                  sx={filterButtonSx}
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={exportDailyReport}
                >下载</Button>
              )}
              <AppDataGridColumnSettingsButton
                apiRef={reportGridApiRef}
                buttonId={columnSettingsButtonId}
                panelId={columnSettingsPanelId}
                label={t('Column Settings')}
                ref={setColumnSettingsAnchorEl}
                type="button"
              />
            </Grid>
          </Grid>
        </Box>
        )}
        <ReportTable
          apiRef={reportGridApiRef}
          data={displayedData}
          columns={columns}
          getColumnLabel={(col) => displayedData?.columnLabels?.[col] || columns[col]?.label || formatFieldName(col)}
          renderCell={renderCellValue}
          getCellTitle={(col, row) => row[col]}
          getRowKey={(row) => row.id}
          page={page}
          rowsPerPage={limit}
          onPagination={handlePagination}
          showColumnSettings={showToolbar}
          columnSettingsTableId={columnSettingsTableId || `report-${dim}`}
          columnSettingsUserId={session?.uid}
          columnSettingsAnchorEl={columnSettingsAnchorEl}
        />
      </Stack>
  );

  return embedded ? reportContent : (
    <Page title={title} styles={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      {reportContent}
    </Page>
  );
};

DailyReports.propTypes = {
  embedded: PropTypes.bool,
  initialSearchParams: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(URLSearchParams),
  ]),
  showToolbar: PropTypes.bool,
  showScopeFilters: PropTypes.bool,
  showDownload: PropTypes.bool,
  showTrackerColumns: PropTypes.bool,
  columnSettingsTableId: PropTypes.string,
};

DailyReports.defaultProps = {
  embedded: false,
  initialSearchParams: '',
  showToolbar: true,
  showScopeFilters: true,
  showDownload: true,
  showTrackerColumns: false,
  columnSettingsTableId: null,
};

export default DailyReports;
